import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { RootState } from 'dashboard/redux/store';
/**
 * @name `useVerificationTemplateReferenceMap`
 *
 * A hook that returns a map of verification template tokens to the
 * name of the inquiry template that it's used in. Used in PermissionsTable
 * and VerificationTemplatesTable to provide additional usage context info in lists.
 */
export default (): Record<string, string> => {
  const inquiryTemplates = useSelector<RootState, InquiryTemplate[]>(
    (state) => state.organization.inquiryTemplates ?? [],
  );
  return useMemo(
    () =>
      inquiryTemplates
        .flatMap((inquiryTemplate) => {
          const { name, 'verification-template-ids': vtmplIds = [] } = inquiryTemplate.attributes;
          return vtmplIds.map((vtmplId) => ({ vtmplId, inquiryTemplateName: name }));
        })
        .reduce(
          (map, { vtmplId, inquiryTemplateName }) => ({
            ...map,
            [vtmplId]: inquiryTemplateName,
          }),
          {},
        ),
    [inquiryTemplates],
  );
};

export const useVerificationTemplateFullReferenceMap = (): Record<string, InquiryTemplate> => {
  const inquiryTemplates = useSelector<RootState, InquiryTemplate[]>(
    (state) => state.organization.inquiryTemplates ?? [],
  );
  return useMemo(
    () =>
      inquiryTemplates
        .flatMap((inquiryTemplate) => {
          const { 'verification-template-ids': vtmplIds = [] } = inquiryTemplate.attributes;
          return vtmplIds.map((vtmplId) => ({ vtmplId, inquiryTemplate }));
        })
        .reduce(
          (map, { vtmplId, inquiryTemplate }) => ({
            ...map,
            [vtmplId]: inquiryTemplate,
          }),
          {},
        ),
    [inquiryTemplates],
  );
};
